/**
 * Timecode helpers. Cue times are seconds (float, millisecond precision);
 * the UI shows and accepts `HH:MM:SS.mmm`, which is what subtitle editors use.
 */

const MS_PER_S = 1000

/** Transcript times are rounded to milliseconds by the spec (§5). */
export function roundToMs(value: number): number {
  return Math.round(value * MS_PER_S) / MS_PER_S
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, value))
}

function pad(value: number, width: number): string {
  return String(Math.trunc(Math.abs(value))).padStart(width, '0')
}

/** `00:01:23.456` — the caption timecode input shape. */
export function formatTimecode(seconds: number): string {
  const totalMs = Math.max(0, Math.round(seconds * MS_PER_S))
  const ms = totalMs % MS_PER_S
  const totalSeconds = (totalMs - ms) / MS_PER_S
  const s = totalSeconds % 60
  const m = Math.floor(totalSeconds / 60) % 60
  const h = Math.floor(totalSeconds / 3600)
  return `${pad(h, 2)}:${pad(m, 2)}:${pad(s, 2)}.${pad(ms, 3)}`
}

/** `1:23` or `1:02:03` — the player clock. */
export function formatClock(seconds: number): string {
  const totalMs = Math.max(0, Math.round(seconds * MS_PER_S))
  const totalSeconds = Math.floor(totalMs / MS_PER_S)
  const s = totalSeconds % 60
  const m = Math.floor(totalSeconds / 60) % 60
  const h = Math.floor(totalSeconds / 3600)
  return h > 0 ? `${h}:${pad(m, 2)}:${pad(s, 2)}` : `${m}:${pad(s, 2)}`
}

const TIMECODE_RE = /^\d{1,4}(:\d{1,2}){0,2}(\.\d{1,3})?$/

/**
 * Accepts `95`, `95.5`, `1:35.5`, `00:01:35.500` and the comma decimal
 * separator Albanian input produces. Returns null when the text is not a
 * timecode so the caller can keep showing the previous value.
 */
export function parseTimecode(input: string): number | null {
  const raw = input.trim().replace(',', '.')
  if (raw === '' || !TIMECODE_RE.test(raw)) return null
  let seconds = 0
  for (const part of raw.split(':')) {
    const value = Number(part)
    if (!Number.isFinite(value)) return null
    seconds = seconds * 60 + value
  }
  return roundToMs(seconds)
}

/** Albanian duration label for lists and the ledger: `1 h 23 min`. */
export function formatDuration(seconds: number | null | undefined, t: { h: string; min: string; s: string }): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h} ${t.h} ${pad(m, 2)} ${t.min}`
  if (m > 0) return `${m} ${t.min} ${pad(s, 2)} ${t.s}`
  return `${s} ${t.s}`
}
